// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Img, { ImgProps } from "@store/MobiusOverrides/Img/Img";
import { headerIconButtonProps } from "@store/Themes/common/props/header";
import useCurrentBreakpointIndex from "@store/Framework/Common/Hooks/useCurrentBreakpointIndex";
import useHeaderHeight from "@store/Framework/Store/Bunzl/Components/Header/Hooks/useHeaderHeight";
import useHeaderOpacity from "@store/Framework/Store/Bunzl/Components/Header/Hooks/useHeaderOpacity";
import Menu from "@store/Library/Components/Icons/Menu";
import RedChevron from "@store/Library/Components/Icons/RedChevron";
import Button, { ButtonIcon, ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import Popover, { PopoverPresentationProps } from "@store/MobiusOverrides/Popover/Popover";
import { MappedLink } from "@store/Overrides/Widgets/Header/MainNavigation";
import { contentSpacingStyles, linkStyles, transitionHoverStyles } from "@store/Themes/common/mixins";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { css, useTheme } from "styled-components";

interface Props {
    menuZoneId: string;
    link: MappedLink;
    backgroundImage?: string;
    backgroundImageStyles: "cover" | "repeat";
}

export interface BigMenuStyles {
    popoverWrapper?: InjectableCss;
    menuContainer?: GridContainerProps;
    menuTriggerButton?: ButtonPresentationProps;
    menuIcon?: IconProps;
    popover?: PopoverPresentationProps;
    leftGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
    linksWrapper?: InjectableCss;
    linksContainer?: GridContainerProps;
    linkGridItem?: GridItemProps;
    linkImg?: ImgProps;
    links?: LinkPresentationProps;
    childLinks?: LinkPresentationProps;
    childList?: TypographyPresentationProps;
    childListItem?: TypographyPresentationProps;
    redChevron?: IconProps;
    titleButton?: ButtonPresentationProps;
}

const bigMenuStyles: BigMenuStyles = {
    popoverWrapper: {
        css: css`
            padding: ${pxToRem(16)} 0 0;
        `,
    },
    menuContainer: {
        offsetCss: css`
            width: 100%;
            margin: 0;
        `,
    },
    menuTriggerButton: headerIconButtonProps,
    menuIcon: {
        color: "primary.contrast",
        size: 28,
        css: css`
            ${transitionHoverStyles()}

            &:hover {
                color: ${getColor("primary")};
            }
        `,
    },
    popover: {
        transitionDuration: "short",
        contentBodyProps: {
            css: css`
                max-height: none;
                display: flex;
                flex-direction: column;
                padding: 0;
                box-shadow: ${({ theme }) => theme.shadows.e80};
                ${x => breakpointValues(x.theme.design.layout.spacing.side, ["right", "left"])}
            `,
        },
        wrapperProps: {
            css: css`
                position: static !important; /* Most be important because it's not overridable */
            `,
        },
    },
    leftGridItem: {
        width: [8, 8, 9, 10, 10],
    },
    rightGridItem: {
        width: [4, 4, 3, 2, 2],
        css: css`
            background-color: ${getColor("common.darkBackground")};
        `,
    },
    linksWrapper: {
        css: css`
            width: 100%;
        `,
    },
    linksContainer: {
        gap: 20,
        css: css`
            padding: ${pxToRem(16, 8)};
        `,
    },
    linkGridItem: {
        css: css`
            flex-direction: column;
            align-items: center;
            text-align: center;
        `,
    },
    linkImg: {
        css: css`
            max-width: ${pxToRem(80)};
            height: auto;
            margin-bottom: ${pxToRem(8)};
        `,
    },
    links: {
        css: css`
            ${linkStyles("text", "text", false, false)}
            display: flex;
            justify-content: center;
            width: 100%;
        `,
        typographyProps: {
            css: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 100%;
                margin-bottom: ${pxToRem(8)};

                ${({ theme }) => `
                    font-weight: ${theme.design.typography.weight.bold};
                    font-size: ${theme.design.typography.size.base};
                `};
            `,
        },
    },
    childLinks: {
        css: css`
            ${linkStyles("text", "primary", false, true)}
            ${({ theme }) => `
                font-weight: ${theme.design.typography.weight.semibold};
                font-size: ${theme.design.typography.size.small};
            `};
        `,
    },
    childList: {
        css: css`
            text-align: center;
        `,
    },
    childListItem: {
        css: css`
            ${contentSpacingStyles(0, 0, pxToRem(8))}
        `,
    },
    redChevron: {
        css: css`
            width: 100%;
            justify-content: flex-end;
            background-color: transparent;

            svg {
                width: auto;
            }
        `,
    },
    titleButton: {
        variant: "primary",
        css: css`
            ${x => x.theme.button.primary.css}
            min-height: inherit;
            padding: 0.3em;
            width: 100%;

            &:hover {
                border-color: ${getColor("primary")};
                color: ${getColor("primary")};
            }
        `,
        hoverStyle: {
            backgroundColor: "transparent",
        },
        typographyProps: {
            css: css`
                font-size: ${({ theme }) => theme.design.typography.size.small};
                text-transform: uppercase;
            `,
        },
    },
};

const styles = bigMenuStyles;

const BigMenu: FC<Props> = ({ menuZoneId, link, backgroundImage, backgroundImageStyles }) => {
    const popoverRef = useRef<HTMLUListElement>(null);
    const elementRef = useRef<HTMLUListElement>(null);
    const shellContext = useContext(ShellContext);
    const theme = useTheme();
    const { manuallySetFullOpacity } = useHeaderOpacity();
    const { headerHeight } = useHeaderHeight();
    const currentBreakpoint = useCurrentBreakpointIndex();
    const shellTop = "-150px";

    const themeSpacing = theme.design.layout.spacing.side;
    const spacing = String(themeSpacing[currentBreakpoint]);

    const closePopover = () => {
        popoverRef.current && (popoverRef.current! as any).closePopover();
    };

    const onPopoverOpen = () => {
        manuallySetFullOpacity(true);
    };

    const onPopoverClose = () => {
        manuallySetFullOpacity(false);
    };

    const numberOfColumns = link?.numberOfColumns || 6;
    const desktopWidthLinkGridItem = (12 / numberOfColumns) as GridWidths;

    const linkGridItemStyle: GridItemProps = {
        ...styles.linkGridItem,
        width: [6, 6, 4, desktopWidthLinkGridItem, desktopWidthLinkGridItem],
        css: css`
            ${styles?.linkGridItem?.css}
            border-right: ${pxToRem(1)} solid ${getColor("common.border")};

            &:nth-child(${numberOfColumns}n) {
                border: none;
            }

            ${breakpoint("md", "down")} {
                &:nth-child(${numberOfColumns}n) {
                    border-right: ${pxToRem(1)} solid ${getColor("common.border")};
                }

                &:nth-child(3n) {
                    border: none;
                }
            }
        `,
    };

    const leftGridItem = {
        ...styles.leftGridItem,
        width: shellContext.isInShell && shellContext.isEditing ? 8 : styles.leftGridItem?.width,
    };

    const rightGridItem = {
        ...styles.rightGridItem,
        width: shellContext.isInShell && shellContext.isEditing ? 4 : styles.rightGridItem?.width,
    };

    return (
        <Popover
            {...styles.popover}
            contentBodyProps={{
                ...styles.popover?.contentBodyProps,
                as: "div",
                ref: elementRef,
                css: css`
                    ${styles.popover?.contentBodyProps?.css}
                    ${shellContext.isInShell && shellContext.isEditing
                        ? css`
                              width: 1250px;
                          `
                        : null}
                    ${backgroundImage && ` background-image: url(${backgroundImage});`}
                    ${backgroundImageStyles === "cover" && ` background-size: cover;`}
                    background-position: center
                `,
            }}
            positionFunction={() => {
                return {
                    position: "absolute",
                    top: shellContext.isInShell && shellContext.isEditing ? shellTop : headerHeight.mainHeight,
                    right: spacing,
                    left: spacing,
                };
            }}
            ref={popoverRef}
            insideRefs={[elementRef]}
            onOpen={onPopoverOpen}
            onClose={onPopoverClose}
            canClickOutside={shellContext.isInShell && shellContext.isEditing}
            popoverTrigger={
                <Button
                    aria-label={translate("menu")}
                    {...styles.menuTriggerButton}
                    data-test-selector="expandMobileMenu"
                >
                    <ButtonIcon src={Menu} {...styles.menuIcon} />
                </Button>
            }
        >
            <GridContainer {...styles.menuContainer}>
                <GridItem {...leftGridItem}>
                    <StyledWrapper as="nav" {...styles.linksWrapper}>
                        <GridContainer {...styles.linksContainer}>
                            {link?.children?.map((child, index) => (
                                <GridItem
                                    {...linkGridItemStyle}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                >
                                    {link.removeFirstLevelLink ? (
                                        <Typography {...styles.links?.typographyProps}>
                                            {child.icon && <Img src={child.icon} {...styles.linkImg} />}
                                            {child.title}
                                        </Typography>
                                    ) : (
                                        <Link href={child.url} onClick={closePopover} {...styles.links}>
                                            {child.icon && <Img src={child.icon} {...styles.linkImg} />}
                                            <Button {...styles.titleButton}>{child.title}</Button>
                                        </Link>
                                    )}
                                    {child.children && child.children.length > 0 ? (
                                        <Typography as="ul" {...styles.childList}>
                                            {child.children
                                                .filter(grandChild => !grandChild.excludeFromNavigation)
                                                .map((grandChild, index) => {
                                                    return (
                                                        <Typography
                                                            as="li"
                                                            // eslint-disable-next-line react/no-array-index-key
                                                            key={index}
                                                            {...styles.childListItem}
                                                        >
                                                            <Link
                                                                onClick={closePopover}
                                                                href={grandChild.url}
                                                                {...styles.childLinks}
                                                            >
                                                                {grandChild.title}
                                                            </Link>
                                                        </Typography>
                                                    );
                                                })}
                                        </Typography>
                                    ) : null}
                                </GridItem>
                            ))}
                        </GridContainer>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...rightGridItem}>
                    <StyledWrapper onClick={closePopover}>
                        <Zone contentId={menuZoneId} zoneName="menuZone" />
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <Icon src={RedChevron} {...styles.redChevron} />
        </Popover>
    );
};

export default BigMenu;
